import { useCallback, useRef } from "react";

export function useDebounce<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
  onBlocked?: () => void,
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCallTimeRef = useRef<number>(0);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTimeRef.current;

      if (timeSinceLastCall > delay) {
        // If enough time has passed, execute immediately
        lastCallTimeRef.current = now;
        callback(...args);
        return;
      }

      // If within debounce period, trigger the blocked event
      if (onBlocked) {
        onBlocked();
      }

      // Clear any previous scheduled execution
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule execution to refresh debounce window, but it won't run again
      timeoutRef.current = setTimeout(() => {
        lastCallTimeRef.current = Date.now();
        timeoutRef.current = null;
      }, delay);
    },
    [callback, delay, onBlocked],
  ) as T;
}
